import { socket } from "../core/socket";
import type { GalleryEntryDTO } from "../../shared/types";

/** Filesystem-safe suggested name for a downloaded drawing. */
function downloadName(entry: GalleryEntryDTO): string {
  const base = `${entry.artistName} - ${entry.word}`.replace(/[\\/:*?"<>|]+/g, "").trim();
  return `${base || "drawing"}.png`;
}

const SVG_NS = "http://www.w3.org/2000/svg";

function buildDownloadIcon(): SVGElement {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("width", "16");
  svg.setAttribute("height", "16");
  svg.setAttribute("fill", "none");
  svg.setAttribute("stroke", "currentColor");
  svg.setAttribute("stroke-width", "2");
  svg.setAttribute("stroke-linecap", "round");
  svg.setAttribute("stroke-linejoin", "round");
  svg.setAttribute("aria-hidden", "true");
  const path = document.createElementNS(SVG_NS, "path");
  path.setAttribute("d", "M12 3v12m0 0l-4-4m4 4l4-4M4 20h16");
  svg.appendChild(path);
  return svg;
}

function chip(text: string, className = ""): HTMLElement {
  const el = document.createElement("span");
  el.className = `gallery-chip ${className}`.trim();
  el.textContent = text;
  return el;
}

/** One drawing. The picture is fetched into memory as a blob rather than left as
 * a plain remote <img>: the server deletes saved drawings the moment the game is
 * over, and a popup that's still open then (the host started a new game, say)
 * must keep working - both the picture and its download button. */
function buildEntry(entry: GalleryEntryDTO, objectUrls: string[]): HTMLElement {
  const card = document.createElement("article");
  card.className = "gallery-entry";

  const header = document.createElement("div");
  header.className = "gallery-entry-header";

  const heading = document.createElement("div");
  heading.className = "gallery-entry-heading";

  const title = document.createElement("h3");
  title.className = "gallery-entry-title";
  const artist = document.createElement("span");
  artist.className = "gallery-artist";
  artist.textContent = entry.artistName;
  artist.style.color = entry.artistColor;
  const phrase = document.createElement("span");
  phrase.className = "gallery-phrase";
  phrase.textContent = `“${entry.word}”`;
  title.append(artist, " drew ", phrase);

  const meta = document.createElement("div");
  meta.className = "gallery-meta";
  meta.append(chip(`Round ${entry.round} · Turn ${entry.turn}`), chip(`+${entry.points} points`, "gallery-chip--points"));
  for (const name of entry.cccNames) meta.appendChild(chip(name, "gallery-chip--ccc"));
  if (entry.cccNames.length === 0) meta.appendChild(chip("No Chaos Canvas Cards", "gallery-chip--muted"));

  heading.append(title, meta);

  const download = document.createElement("a");
  download.className = "secondary-button gallery-download";
  download.setAttribute("aria-disabled", "true");
  download.download = downloadName(entry);
  download.append(buildDownloadIcon(), document.createTextNode("Download"));

  header.append(heading, download);

  const frame = document.createElement("div");
  frame.className = "gallery-frame gallery-frame--loading";
  const image = document.createElement("img");
  image.className = "gallery-image";
  image.alt = `${entry.artistName}'s drawing of "${entry.word}"`;
  image.width = 1100;
  image.height = 750;
  frame.appendChild(image);

  fetch(entry.imageUrl)
    .then((res) => {
      if (!res.ok) throw new Error(String(res.status));
      return res.blob();
    })
    .then((blob) => {
      const url = URL.createObjectURL(blob);
      objectUrls.push(url);
      image.src = url;
      download.href = url;
      download.removeAttribute("aria-disabled");
      frame.classList.remove("gallery-frame--loading");
    })
    .catch(() => {
      frame.classList.remove("gallery-frame--loading");
      frame.classList.add("gallery-frame--missing");
      frame.textContent = "This drawing is no longer available.";
    });

  card.append(header, frame);
  return card;
}

/** Opens the "Gallery" popup: every drawing saved this game, oldest first. Closes
 * with the X button, by clicking outside the popup, or with Escape. It's its own
 * overlay on the page (not part of any game screen), so it stays open if the
 * game moves on underneath it. */
export function openGallery(): void {
  if (document.getElementById("gallery-overlay")) return;

  const objectUrls: string[] = [];

  const overlay = document.createElement("div");
  overlay.id = "gallery-overlay";
  overlay.className = "overlay";

  const modal = document.createElement("div");
  modal.id = "gallery-modal";
  modal.setAttribute("role", "dialog");
  modal.setAttribute("aria-modal", "true");
  modal.setAttribute("aria-label", "Gallery");

  const header = document.createElement("div");
  header.className = "gallery-header";
  const heading = document.createElement("div");
  const title = document.createElement("h2");
  title.textContent = "Gallery";
  const subtitle = document.createElement("p");
  subtitle.className = "gallery-subtitle";
  subtitle.textContent = "Every drawing from this game";
  heading.append(title, subtitle);
  const closeBtn = document.createElement("button");
  closeBtn.type = "button";
  closeBtn.className = "gallery-close";
  closeBtn.setAttribute("aria-label", "Close gallery");
  closeBtn.textContent = "✕";
  header.append(heading, closeBtn);

  const body = document.createElement("div");
  body.className = "gallery-body";
  const loading = document.createElement("p");
  loading.className = "hint-text gallery-empty";
  loading.textContent = "Loading the gallery...";
  body.appendChild(loading);

  modal.append(header, body);
  overlay.appendChild(modal);
  document.body.appendChild(overlay);
  closeBtn.focus();

  function close(): void {
    document.removeEventListener("keydown", onKeyDown);
    overlay.remove();
    for (const url of objectUrls) URL.revokeObjectURL(url);
  }
  function onKeyDown(e: KeyboardEvent): void {
    if (e.key === "Escape") close();
  }
  document.addEventListener("keydown", onKeyDown);
  closeBtn.addEventListener("click", close);
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) close();
  });

  socket.emit("gallery:request", (entries) => {
    if (!overlay.isConnected) return;
    body.innerHTML = "";
    if (entries.length === 0) {
      const empty = document.createElement("p");
      empty.className = "hint-text gallery-empty";
      empty.textContent = "No drawings were saved this game.";
      body.appendChild(empty);
      return;
    }
    subtitle.textContent = `${entries.length} ${entries.length === 1 ? "drawing" : "drawings"} from this game`;
    for (const entry of entries) body.appendChild(buildEntry(entry, objectUrls));
  });
}
